import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, Users, FolderKanban, Loader2 } from 'lucide-react';
import {
  AMBER, NAVY, BORDER_COLOR, BG_FIELD,
  bubbleStyle, navyBtn, fieldStyle, selectStyle, outlineBtn,
} from '../../styles/tokens';
import client from '../../api/client';

const ROLE_LABELS = {
  worker: 'Trabajador',
  supervisor: 'Supervisor(a)',
  engineer: 'Ingeniero(a)',
  manager: 'Gerente',
};

const ROLE_COLORS = {
  worker: '#64748b',
  supervisor: AMBER,
  engineer: '#2563eb',
  manager: '#7c3aed',
};

export default function ProjectEditPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const isCreateMode = projectId === 'nuevo';

  // ── Project details ──
  const [project, setProject] = useState(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [projectClient, setProjectClient] = useState('');
  const [approvalType, setApprovalType] = useState('2');
  const [projectType, setProjectType] = useState('project');
  const [codingMode, setCodingMode] = useState('tareas');
  const [finalReviewerId, setFinalReviewerId] = useState('');
  const [nominaReviewerId, setNominaReviewerId] = useState('');
  const [expensesReviewerId, setExpensesReviewerId] = useState('');
  const [amgtimeDeptCode, setAmgtimeDeptCode] = useState('');
  const [isTesting, setIsTesting] = useState(false);

  // ── Users by role for leadership pickers ──
  const [supervisorUsers, setSupervisorUsers] = useState([]);
  const [engineerUsers, setEngineerUsers] = useState([]);
  const [managerUsers, setManagerUsers] = useState([]);
  const [reviewerUsers, setReviewerUsers] = useState([]);

  // ── Cost codes ──
  const [projectCostCodes, setProjectCostCodes] = useState([]);

  // ── Cost centers per tarea (from existing Códigos table) ──
  const [costCentersList, setCostCentersList] = useState([]); // all active cost centers
  const [tareaCCMappings, setTareaCCMappings] = useState({}); // { costCodeId: Set(costCenterIds) }
  const [tareaCCDirty, setTareaCCDirty] = useState(false); // only save mappings if user changed them

  // ── Single ganga ──
  const [gangaId, setGangaId] = useState(null); // null = new ganga
  const [gangaMembers, setGangaMembers] = useState([]); // flat array of all members
  const [workers, setWorkers] = useState([]); // personas for worker dropdown

  // ── Page state ──
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [error, setError] = useState('');

  // ── Initial load ──
  useEffect(() => {
    // Always load role-based user lists
    Promise.all([
      client.get('/api/nomina/users-by-role?role=supervisor').catch(() => ({ data: [] })),
      client.get('/api/nomina/users-by-role?role=engineer').catch(() => ({ data: [] })),
      client.get('/api/nomina/users-by-role?role=manager').catch(() => ({ data: [] })),
      client.get('/api/nomina/users-by-role?role=reviewer').catch(() => ({ data: [] })),
    ]).then(([supRes, engRes, mgrRes, revRes]) => {
      setSupervisorUsers(supRes.data || []);
      setEngineerUsers(engRes.data || []);
      setManagerUsers(mgrRes.data || []);
      setReviewerUsers(revRes.data || []);
    });

    // Fetch cost centers (from existing Códigos table, for tarea-CC mapping)
    client.get('/api/admin/cost-centers')
      .then(r => setCostCentersList((r.data || []).filter(cc => cc.active !== false)))
      .catch(() => {});

    if (isCreateMode) {
      Promise.all([
        client.get('/api/admin/cost-codes').catch(() => ({ data: [] })),
        client.get('/api/nomina/workers').catch(() => ({ data: [] })),
      ]).then(([ccRes, workersRes]) => {
        setProjectCostCodes((ccRes.data || []).map(cc => ({ ...cc, linked: false })));
        setWorkers(workersRes.data || []);
        // Pre-populate leadership slots for 2-level
        setGangaMembers(getLeadershipSlots('2').map(s => ({
          user_id: '', role: s.role,
        })));
      }).catch(err => { console.error(err); setError('Error al cargar datos'); })
        .finally(() => setPageLoading(false));
    } else {
      Promise.all([
        client.get('/api/admin/projects'),
        client.get('/api/admin/cost-codes').catch(() => ({ data: [] })),
        client.get(`/api/nomina/projects/${projectId}/gangas`).catch(() => ({ data: [] })),
        client.get('/api/nomina/workers').catch(() => ({ data: [] })),
      ]).then(([projRes, ccRes, gangasRes, workersRes]) => {
        const found = projRes.data.find(p => String(p.id) === String(projectId));
        if (!found) { setError('Proyecto no encontrado'); setPageLoading(false); return; }

        setProject(found);
        setName(found.name || '');
        setCode(found.code || '');
        setProjectClient(found.client || '');
        setApprovalType(found.approval_type === '3_levels' ? '3' : '2');
        setProjectType(found.project_type || (found.is_admin_project ? 'administrative' : 'project'));
        setCodingMode(found.nomina_coding_mode || 'tareas');
        setFinalReviewerId(found.final_reviewer_id ? String(found.final_reviewer_id) : '');
        setNominaReviewerId(found.nomina_reviewer_id ? String(found.nomina_reviewer_id) : '');
        setExpensesReviewerId(found.expenses_reviewer_id ? String(found.expenses_reviewer_id) : '');
        setAmgtimeDeptCode(found.amgtime_dept_code || '');
        setIsTesting(found.is_testing === true);
        setWorkers(workersRes.data || []);

        // Load cost codes
        client.get(`/api/admin/projects/${projectId}/cost-codes`)
          .then(res => setProjectCostCodes(res.data))
          .catch(() => {});

        // Load tarea-cost center mappings
        client.get(`/api/admin/projects/${projectId}/tarea-cost-centers`)
          .then(res => {
            const map = {};
            for (const row of (res.data || [])) {
              if (!map[row.cost_code_id]) map[row.cost_code_id] = new Set();
              map[row.cost_code_id].add(row.cost_center_id);
            }
            setTareaCCMappings(map);
          })
          .catch(() => {});

        // Load first ganga (single ganga per project)
        const gangas = gangasRes.data || [];
        if (gangas.length > 0) {
          const g = gangas[0];
          setGangaId(g.id);
          // Map API members to internal format
          const approvalTypeVal = found.approval_type === '3_levels' ? '3' : '2';
          const leadershipFromApi = (g.members || [])
            .filter(m => ['supervisor', 'engineer', 'manager'].includes(m.role))
            .map(m => ({ user_id: String(m.user_id || ''), role: m.role }));
          const workersFromApi = (g.members || [])
            .filter(m => m.role === 'worker')
            .map(m => ({ persona_id: String(m.persona_id || ''), role: 'worker' }));

          // V30.1: load ALL ganga leadership members (any-of approval). The previous
          // per-slot .find() collapsed multiple engineers/managers to one.
          // No auto-clear hack needed — V30 validation accepts ≥1 supervisor OR engineer
          // for 2-level, ≥1 of each for 3-level.
          const result = [...leadershipFromApi, ...workersFromApi];
          setGangaMembers(result);
        } else {
          // No ganga yet — pre-populate leadership slots
          const approvalTypeVal = found.approval_type === '3_levels' ? '3' : '2';
          setGangaMembers(getLeadershipSlots(approvalTypeVal).map(s => ({
            user_id: '', role: s.role,
          })));
        }
      }).catch(err => { console.error(err); setError('Error al cargar datos del proyecto'); })
        .finally(() => setPageLoading(false));
    }
  }, [projectId]);

  // When approval type changes, re-initialize leadership slots (keep existing assignments if role matches)
  function handleApprovalTypeChange(newType) {
    setApprovalType(newType);
    const slots = getLeadershipSlots(newType);
    const currentLeadership = gangaMembers.filter(m => ['supervisor', 'engineer', 'manager'].includes(m.role));
    const currentWorkers = gangaMembers.filter(m => m.role === 'worker');
    const newLeadership = slots.map(slot => {
      return currentLeadership.find(m => m.role === slot.role) || { user_id: '', role: slot.role };
    });
    setGangaMembers([...newLeadership, ...currentWorkers]);
  }

  // ── Helpers ──
  function getLeadershipSlots(approvalTypeVal) {
    const is3 = approvalTypeVal === '3' || approvalTypeVal === '3_levels';
    return [
      { role: 'supervisor', required: is3 },
      { role: 'engineer',   required: is3 },
      { role: 'manager',    required: is3 },
    ];
  }

  function getUsersForRole(role) {
    if (role === 'supervisor') return supervisorUsers;
    if (role === 'engineer') return engineerUsers;
    if (role === 'manager') return managerUsers;
    return [];
  }

  function serializeMembers(members) {
    return members.filter(m => m.user_id || m.persona_id).map(m => {
      if (['supervisor', 'engineer', 'manager'].includes(m.role)) {
        return { user_id: Number(m.user_id), role: m.role };
      }
      return { persona_id: Number(m.persona_id), role: m.role };
    });
  }

  function validateGangaComposition(members) {
    const leaders = members.filter(m => ['supervisor', 'engineer', 'manager'].includes(m.role) && m.user_id);
    const is3 = approvalType === '3' || approvalType === '3_levels';
    // V30 / Sprint Cambio 1: each role allows multiple (any-of approval).
    if (is3) {
      if (!leaders.some(m => m.role === 'supervisor')) return 'Nivel 3 requiere al menos un supervisor(a).';
      if (!leaders.some(m => m.role === 'engineer'))   return 'Nivel 3 requiere al menos un ingeniero(a).';
      if (!leaders.some(m => m.role === 'manager'))    return 'Nivel 3 requiere al menos un gerente.';
    } else {
      // 2-level: needs ≥1 supervisor OR ≥1 engineer (sprint Cambio 1, relaxed from "2 of 3").
      if (!leaders.some(m => m.role === 'supervisor' || m.role === 'engineer')) {
        return 'Nivel 2 requiere al menos un supervisor(a) o ingeniero(a).';
      }
    }
    return null;
  }

  function handleToggleTarea(ccId) {
    setProjectCostCodes(prev => prev.map(cc => cc.id === ccId ? { ...cc, linked: !cc.linked } : cc));
  }

  // ── Single save ──
  async function handleSave() {
    if (!name.trim()) { alert('El nombre del proyecto es requerido.'); return; }

    // Cuadrilla validation only for non-admin projects
    if (projectType !== 'administrative') {
      const gangaError = validateGangaComposition(gangaMembers);
      if (gangaError) { alert(gangaError); return; }
    }

    setSaving(true);
    setSaveMsg('');
    try {
      const projectPayload = {
        name: name.trim(),
        code: code.trim() || null,
        client: projectClient.trim() || null,
        approval_type: approvalType === '3' ? '3_levels' : '2_levels',
        project_type: projectType,
        nomina_coding_mode: codingMode,
        isAdminProject: projectType === 'administrative',
        is_testing: isTesting,
        final_reviewer_id: projectType === 'administrative' ? (finalReviewerId ? parseInt(finalReviewerId) : null) : null,
        nomina_reviewer_id: projectType !== 'administrative' ? (nominaReviewerId ? parseInt(nominaReviewerId) : null) : undefined,
        expenses_reviewer_id: projectType !== 'administrative' ? (expensesReviewerId ? parseInt(expensesReviewerId) : null) : undefined,
        amgtime_dept_code: amgtimeDeptCode.trim() || null,
      };

      let savedProjectId = projectId;

      if (isCreateMode) {
        const projRes = await client.post('/api/admin/projects', projectPayload);
        savedProjectId = projRes.data.id;
      } else {
        await client.patch(`/api/admin/projects/${projectId}`, projectPayload);
      }

      // Save tareas
      const linked = projectCostCodes.filter(cc => cc.linked).map(cc => cc.id);
      await client.put(`/api/admin/projects/${savedProjectId}/cost-codes`, { costCodeIds: linked });

      // Save tarea-cost center mappings (only if user actually changed them)
      if (tareaCCDirty) {
        const mappings = Object.entries(tareaCCMappings)
          .filter(([ccId]) => linked.includes(parseInt(ccId)))
          .map(([costCodeId, ccIdSet]) => ({
            costCodeId: parseInt(costCodeId),
            costCenterIds: [...ccIdSet],
          }))
          .filter(m => m.costCenterIds.length > 0);
        await client.put(`/api/admin/projects/${savedProjectId}/tarea-cost-centers`, { mappings });
      }

      // Save ganga (only for non-admin projects)
      if (projectType === 'administrative') { /* skip ganga for admin projects */ }
      else {
      const validMembers = serializeMembers(gangaMembers);
      if (validMembers.length > 0) {
        if (gangaId) {
          await client.put(`/api/nomina/gangas/${gangaId}`, { members: validMembers });
        } else {
          const gangaRes = await client.post(`/api/nomina/projects/${savedProjectId}/gangas`, { members: validMembers });
          setGangaId(gangaRes.data.id);
        }
      }
      }

      // Navigate back to project list after successful save
      navigate('/admin/tablas', { state: { tab: 'projects' } });
    } catch (err) {
      setSaveMsg(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  // ── Render leadership slots ──
  // V30: each role (supervisor/engineer/manager) supports MULTIPLE assignees (any-of approval).
  // Sprint Cambio 1: 2-level needs ≥1 supervisor or engineer; 3-level needs ≥1 of all three.
  function renderLeadershipSlots() {
    const slots = getLeadershipSlots(approvalType);

    function updateMemberAt(absoluteIdx, newUserId) {
      const updated = [...gangaMembers];
      if (!newUserId) {
        updated.splice(absoluteIdx, 1);
      } else {
        updated[absoluteIdx] = { ...updated[absoluteIdx], user_id: newUserId };
      }
      setGangaMembers(updated);
    }
    function removeMemberAt(absoluteIdx) {
      const updated = [...gangaMembers];
      updated.splice(absoluteIdx, 1);
      setGangaMembers(updated);
    }
    function addMemberForRole(role) {
      setGangaMembers([...gangaMembers, { user_id: '', role }]);
    }

    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontSize: 12, fontWeight: 700, color: NAVY, textTransform: 'uppercase',
          letterSpacing: 0.5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: AMBER, display: 'inline-block' }} />
          Liderazgo
        </div>
        {slots.map(slot => {
          const userList = getUsersForRole(slot.role);
          // Absolute indices into gangaMembers for every member of this role (preserves identity on update).
          const indicesForRole = gangaMembers
            .map((m, idx) => (m.role === slot.role ? idx : -1))
            .filter(idx => idx >= 0);

          return (
            <div key={`role-${slot.role}`} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: ROLE_COLORS[slot.role] || NAVY, marginBottom: 4 }}>
                {ROLE_LABELS[slot.role]}{slot.required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
                {indicesForRole.length > 1 && (
                  <span style={{ marginLeft: 6, fontWeight: 400, color: '#94a3b8' }}>
                    · cualquiera puede aprobar
                  </span>
                )}
              </div>
              {indicesForRole.length === 0 && (
                <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginBottom: 4 }}>
                  Sin asignar — usa el botón abajo.
                </div>
              )}
              {indicesForRole.map((absIdx, rowIdx) => {
                const m = gangaMembers[absIdx];
                return (
                  <div key={`${slot.role}-${rowIdx}`} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    <select
                      value={m.user_id ? String(m.user_id) : ''}
                      onChange={e => updateMemberAt(absIdx, e.target.value)}
                      style={{ ...selectStyle, flex: 1 }}
                    >
                      <option value="">— Seleccionar {ROLE_LABELS[slot.role].toLowerCase()} —</option>
                      {userList.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeMemberAt(absIdx)}
                      title="Quitar"
                      style={{
                        background: '#fff', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                        color: '#dc2626', cursor: 'pointer', padding: '5px 8px',
                        fontFamily: 'Inter, sans-serif', fontSize: 12, fontWeight: 600,
                      }}
                    >✕</button>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={() => addMemberForRole(slot.role)}
                style={{
                  marginTop: 2, fontSize: 11, color: AMBER,
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  fontFamily: 'Inter, sans-serif', fontWeight: 600,
                }}
              >+ Agregar {ROLE_LABELS[slot.role].toLowerCase()}</button>
            </div>
          );
        })}
      </div>
    );
  }

  // ── Render worker rows ──
  function renderWorkerRows() {
    const workerMembers = gangaMembers.filter(m => m.role === 'worker');
    return (
      <div>
        <div style={{
          fontSize: 12, fontWeight: 700, color: NAVY, textTransform: 'uppercase',
          letterSpacing: 0.5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#64748b', display: 'inline-block' }} />
          Trabajadores
        </div>
        {workerMembers.length === 0 && (
          <p style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', marginBottom: 6 }}>
            Sin trabajadores asignados — todos los empleados estarán disponibles.
          </p>
        )}
        {workerMembers.map((m, wIdx) => {
          let count = 0;
          const actualIdx = gangaMembers.findIndex(mem => {
            if (mem.role === 'worker') { if (count === wIdx) return true; count++; }
            return false;
          });
          return (
            <div key={`worker-${wIdx}`} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <select
                value={m.persona_id ? String(m.persona_id) : ''}
                onChange={e => {
                  const updated = [...gangaMembers];
                  updated[actualIdx] = { ...updated[actualIdx], persona_id: e.target.value };
                  setGangaMembers(updated);
                }}
                style={{ ...selectStyle, flex: 2 }}
              >
                <option value="">— Seleccionar trabajador —</option>
                {workers.map(w => <option key={w.id} value={String(w.id)}>{w.name}</option>)}
              </select>
              <div style={{
                flex: 1, padding: '7px 10px', fontSize: 13,
                background: BG_FIELD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                color: '#64748b', fontWeight: 600,
              }}>
                {ROLE_LABELS.worker}
              </div>
              <button
                onClick={() => setGangaMembers(gangaMembers.filter((_, j) => j !== actualIdx))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4 }}
              >
                <Trash2 size={15} />
              </button>
            </div>
          );
        })}
        <button
          onClick={() => setGangaMembers([...gangaMembers, { persona_id: '', role: 'worker' }])}
          style={{ ...outlineBtn, fontSize: 12, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <Plus size={13} /> Agregar Trabajador
        </button>
      </div>
    );
  }

  // ── Loading / error states ──
  if (pageLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Loader2 size={28} className="spin" style={{ color: AMBER }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32, maxWidth: 700, margin: '0 auto' }}>
        <button
          onClick={() => navigate('/admin/tablas', { state: { tab: 'projects' } })}
          style={{ ...outlineBtn, display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 20 }}
        >
          <ArrowLeft size={16} /> Volver a Proyectos
        </button>
        <div style={{ ...bubbleStyle, textAlign: 'center', color: '#ef4444', fontWeight: 600 }}>{error}</div>
      </div>
    );
  }

  // ── Render ──
  return (
    <div style={{ padding: '24px 32px', maxWidth: 900, margin: '0 auto' }}>
      <button
        onClick={() => navigate('/admin/tablas', { state: { tab: 'projects' } })}
        style={{ ...outlineBtn, display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 20 }}
      >
        <ArrowLeft size={16} /> Volver a Proyectos
      </button>

      <h1 style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginBottom: 24 }}>
        {isCreateMode ? 'Nuevo Proyecto' : `Editar Proyecto: ${project?.name}`}
      </h1>

      {/* ═══════ Section 1: Detalles ═══════ */}
      <div style={{ ...bubbleStyle, marginBottom: 20 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FolderKanban size={18} style={{ color: AMBER }} />
          Detalles del Proyecto
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Nombre *</label>
            <input value={name} onChange={e => setName(e.target.value)} style={fieldStyle} placeholder="Nombre del proyecto" />
          </div>
          <div>
            <label style={labelStyle}>Código</label>
            <input value={code} onChange={e => setCode(e.target.value)} style={fieldStyle} placeholder="Ej: PRY-001" />
          </div>
          <div>
            <label style={labelStyle}>Cliente</label>
            <input value={projectClient} onChange={e => setProjectClient(e.target.value)} style={fieldStyle} placeholder="Nombre del cliente" />
          </div>
          <div>
            <label style={labelStyle}>Código AMGtime</label>
            <input value={amgtimeDeptCode} onChange={e => setAmgtimeDeptCode(e.target.value.toUpperCase())} style={fieldStyle} placeholder="Ej: LILLY, OTHER" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: projectType !== 'administrative' ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr', gap: 20 }}>
          <div>
            <label style={labelStyle}>Tipo de Proyecto</label>
            <div style={{ display: 'flex', gap: 20, marginTop: 6 }}>
              {['project', 'administrative'].map(val => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14, color: NAVY }}>
                  <input type="radio" name="projectType" value={val} checked={projectType === val} onChange={e => setProjectType(e.target.value)} style={{ accentColor: AMBER }} />
                  {val === 'project' ? 'Proyecto' : 'Administrativo'}
                </label>
              ))}
            </div>
            <label style={{ ...labelStyle, marginTop: 14 }}>Codificación de Nómina</label>
            <div style={{ display: 'flex', gap: 20, marginTop: 6 }}>
              {[{ v: 'tareas', l: 'Tareas' }, { v: 'gl_accounts', l: 'Cuentas GL' }].map(({ v, l }) => (
                <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14, color: NAVY }}>
                  <input type="radio" name="codingMode" value={v} checked={codingMode === v} onChange={e => setCodingMode(e.target.value)} style={{ accentColor: AMBER }} />
                  {l}
                </label>
              ))}
            </div>
            {/* Proyecto de Prueba: se oculta de TODOS los tableros, reportes y exportaciones,
                pero sigue siendo funcional (subir/aprobar/exportar dentro de sí mismo). */}
            <label style={{ ...labelStyle, marginTop: 14 }}>Modo</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: NAVY, marginTop: 6 }}>
              <input type="checkbox" checked={isTesting} onChange={e => setIsTesting(e.target.checked)} style={{ accentColor: '#d97706', width: 16, height: 16 }} />
              Proyecto de Prueba <span style={{ fontSize: 12, color: '#94a3b8' }}>(oculto de tableros, reportes y exportaciones)</span>
            </label>
          </div>
          {projectType !== 'administrative' && (
          <div>
            <label style={labelStyle}>Tipo de Aprobación</label>
            <div style={{ display: 'flex', gap: 20, marginTop: 6 }}>
              {[{ val: '2', label: '2 Niveles' }, { val: '3', label: '3 Niveles' }].map(({ val, label }) => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14, color: NAVY }}>
                  <input type="radio" name="approvalType" value={val} checked={approvalType === val} onChange={() => handleApprovalTypeChange(val)} style={{ accentColor: AMBER }} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          )}
          {projectType === 'administrative' ? (
            <div>
              <label style={labelStyle}>Aprobador Final</label>
              <select
                value={finalReviewerId}
                onChange={e => setFinalReviewerId(e.target.value)}
                style={{ ...selectStyle, width: '100%', marginTop: 4 }}
              >
                <option value="">— Seleccionar —</option>
                {reviewerUsers.map(u => (
                  <option key={u.id} value={String(u.id)}>{u.name}</option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div>
                <label style={labelStyle}>Aprobador Final — Nómina</label>
                <select
                  value={nominaReviewerId}
                  onChange={e => setNominaReviewerId(e.target.value)}
                  style={{ ...selectStyle, width: '100%', marginTop: 4 }}
                >
                  <option value="">— Seleccionar —</option>
                  {reviewerUsers.map(u => (
                    <option key={u.id} value={String(u.id)}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Aprobador Final — Gastos</label>
                <select
                  value={expensesReviewerId}
                  onChange={e => setExpensesReviewerId(e.target.value)}
                  style={{ ...selectStyle, width: '100%', marginTop: 4 }}
                >
                  <option value="">— Seleccionar —</option>
                  {reviewerUsers.map(u => (
                    <option key={u.id} value={String(u.id)}>{u.name}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ═══════ Section 2: Cuadrilla (only for non-admin projects) ═══════ */}
      {projectType !== 'administrative' && <div style={{ ...bubbleStyle, marginBottom: 20 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: NAVY, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Users size={18} style={{ color: AMBER }} />
          Cuadrilla
        </h2>
        <div style={{
          fontSize: 12, color: '#64748b', marginBottom: 12, padding: '6px 10px',
          background: '#f8fafc', borderRadius: 6, border: `1px solid ${BORDER_COLOR}`,
        }}>
          {approvalType === '3'
            ? 'Nivel 3: Requiere supervisor + ingeniero + gerente.'
            : 'Nivel 2: Requiere supervisor. Opcionalmente puede agregar un ingeniero o gerente.'}
        </div>
        {renderLeadershipSlots()}
        {renderWorkerRows()}

        {/* Validation hint */}
        {(() => {
          const err = validateGangaComposition(gangaMembers);
          return err ? (
            <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8, padding: '4px 8px', background: '#fef2f2', borderRadius: 6 }}>
              {err}
            </div>
          ) : null;
        })()}
      </div>}

      {/* ═══════ Section 3: Tareas / Fases (only for non-admin projects) ═══════ */}
      {projectType !== 'administrative' && (<>
      <div style={{ ...bubbleStyle, marginBottom: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 6px' }}>Tareas / Fases</h3>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          Seleccione las tareas que aplican a este proyecto. Si no selecciona ninguna, todas estarán disponibles.
        </div>
        <div style={{ maxHeight: 300, overflowY: 'auto', border: `1px solid ${BORDER_COLOR}`, borderRadius: 8, padding: 8 }}>
          {projectCostCodes.length === 0 ? (
            <p style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', padding: 16, margin: 0 }}>
              No hay códigos de costo disponibles.
            </p>
          ) : projectCostCodes.map(cc => (
            <label
              key={cc.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
                background: cc.linked ? '#f0fdf4' : 'transparent', fontSize: 13,
              }}
            >
              <input type="checkbox" checked={cc.linked} onChange={() => handleToggleTarea(cc.id)} style={{ accentColor: AMBER }} />
              <span style={{ fontWeight: 600, color: NAVY }}>{cc.code}</span>
              <span style={{ color: '#64748b' }}>{cc.description}</span>
            </label>
          ))}
        </div>
      </div>

      {/* ═══════ Section 4: Centros de Costo por Tarea ═══════ */}
      {projectCostCodes.some(cc => cc.linked) && costCentersList.length > 0 && (
        <div style={{ ...bubbleStyle, marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 6px' }}>Centros de Costo por Tarea</h3>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
            Para cada tarea seleccionada, marque los centros de costo que aplican. Si no marca ninguno, todos estarán disponibles.
          </div>
          <div style={{ maxHeight: 400, overflowY: 'auto', border: `1px solid ${BORDER_COLOR}`, borderRadius: 8, padding: 8 }}>
            {projectCostCodes.filter(cc => cc.linked).map(tarea => {
              const ccSet = tareaCCMappings[tarea.id] || new Set();
              return (
                <div key={tarea.id} style={{ marginBottom: 12 }}>
                  <div style={{
                    fontWeight: 600, color: NAVY, fontSize: 13, padding: '4px 0',
                    borderBottom: `1px solid ${BORDER_COLOR}`, marginBottom: 6,
                  }}>
                    {tarea.code} — {tarea.description}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 12 }}>
                    {costCentersList.map(cc => (
                      <label key={cc.id} style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
                        background: ccSet.has(cc.id) ? '#f0fdf4' : 'transparent',
                        fontSize: 12, minWidth: 140,
                      }}>
                        <input
                          type="checkbox"
                          checked={ccSet.has(cc.id)}
                          onChange={() => {
                            setTareaCCDirty(true);
                            setTareaCCMappings(prev => {
                              const next = { ...prev };
                              const set = new Set(next[tarea.id] || []);
                              set.has(cc.id) ? set.delete(cc.id) : set.add(cc.id);
                              next[tarea.id] = set;
                              return next;
                            });
                          }}
                          style={{ accentColor: AMBER }}
                        />
                        <span style={{ color: '#475569' }}>{cc.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>)}

      {/* ═══════ Single Save Button ═══════ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          onClick={handleSave}
          disabled={saving || !name.trim()}
          style={{
            ...navyBtn,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 14, padding: '10px 28px',
            opacity: saving || !name.trim() ? 0.6 : 1,
          }}
        >
          {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
          Guardar Proyecto
        </button>
        {saveMsg && (
          <span style={{
            fontSize: 13, fontWeight: 600,
            color: saveMsg.includes('Error') ? '#ef4444' : '#16a34a',
          }}>
            {saveMsg}
          </span>
        )}
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#64748b',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};
